/**
 * Company, "Teslyn Support" (decision 800; help `cloud.support-access`; docs/specs/cloud/support-access.md): a tenant
 * admin lets the Teslyn Support team see (or see and change) the company's data for a set time, ends it early, and
 * reads what the team opened and changed. Managers read it; nobody else sees the tab. Without a grant in force the
 * team sees none of the company's projects, flights, reports or settings.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  SUPPORT_DURATIONS,
  SUPPORT_SCOPE_DETAIL,
  SUPPORT_SCOPE_LABEL,
  SUPPORT_SCOPES,
  SUPPORT_STATE_LABEL,
  checkSupportGrant,
  supportActionWords,
  supportGrantState,
  type SupportGrant,
  type SupportScope,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextArea } from '../components/Fields.tsx';
import { ErrorState, Loading, SectionHeader } from '../components/States.tsx';
import { grantSupport, loadSupportOverview, revokeSupport, type SupportOverview } from './api.ts';

const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function SupportAccessPanel({
  sb,
  tenantId,
  tenantName,
  roles,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly roles: readonly string[];
}): ReactElement {
  const admin = roles.includes('tenant_admin');
  const [data, setData] = useState<SupportOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<SupportScope>('read');
  const [hours, setHours] = useState<string>('24');
  const [reason, setReason] = useState('');
  const [issue, setIssue] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    loadSupportOverview(sb, tenantId)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(load, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading what="Teslyn Support access" />;
  if (data.grants === null) {
    return (
      <p className="body muted">
        Teslyn Support access is not available yet: Teslyn Cloud has not been updated for it.
      </p>
    );
  }
  const now = new Date();
  const current = data.grants.filter((g) => ['active', 'upcoming'].includes(supportGrantState(g, now)));
  const past = data.grants.filter((g) => !current.includes(g));

  function submit(e: FormEvent): void {
    e.preventDefault();
    setIssue(null);
    setNotice(null);
    const h = Number(hours);
    const problem = checkSupportGrant({ scope, hours: h, reason });
    if (problem) {
      setIssue(problem);
      return;
    }
    setBusy(true);
    grantSupport(sb, tenantId, scope, h, reason)
      .then((g) => {
        setReason('');
        setNotice(
          `Teslyn Support can ${scope === 'read' ? 'see' : 'see and change'} ${tenantName}'s data until ${when(g.ends_at)}.`,
        );
        load();
      })
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)))
      .finally(() => setBusy(false));
  }

  function end(g: SupportGrant): void {
    setIssue(null);
    setNotice(null);
    revokeSupport(sb, g.id)
      .then(() => {
        setNotice('Teslyn Support access ended. The support team no longer sees your data.');
        load();
      })
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)));
  }

  return (
    <div className="stack">
      <SectionHeader
        title="Teslyn Support"
        subtitle={`The Teslyn Support team sees none of ${tenantName}'s projects, flights, reports or settings unless a tenant admin lets them, for a set time. Every page they open and every change they make is recorded below.`}
      />

      <section className="panel stack" aria-label="Support access in force">
        <h2 className="heading">Access in force</h2>
        {current.length === 0 ? (
          <p className="body">None: the Teslyn Support team cannot see your data.</p>
        ) : (
          <ul className="stack">
            {current.map((g) => (
              <li key={g.id} className="row">
                <span className="chip chip--warn caption">
                  {SUPPORT_STATE_LABEL[supportGrantState(g, now)]}
                </span>
                <span className="body">
                  {SUPPORT_SCOPE_LABEL[g.scope]}, {when(g.starts_at)} to {when(g.ends_at)}
                  {g.reason ? `: ${g.reason}` : ''}
                  {g.first_used_at ? ` (first opened ${when(g.first_used_at)})` : ' (not opened yet)'}
                </span>
                {admin && (
                  <Button variant="secondary" onClick={() => end(g)}>
                    End now
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {admin ? (
        <form className="panel stack" aria-label="Grant support access" onSubmit={submit} noValidate>
          <h2 className="heading">Let Teslyn Support in</h2>
          <p className="body">
            Grant access when the support team asks for it, for example to look into a question about a
            project. It ends by itself at the time shown; you can end it sooner.
          </p>
          <SelectField
            label="What the support team may do"
            value={scope}
            onChange={setScope}
            options={SUPPORT_SCOPES.map((s) => [s, SUPPORT_SCOPE_LABEL[s]] as const)}
            hint={SUPPORT_SCOPE_DETAIL[scope]}
          />
          <SelectField
            label="For how long"
            value={hours}
            onChange={setHours}
            options={SUPPORT_DURATIONS.map((d) => [String(d.hours), d.label] as const)}
          />
          <TextArea label="Why (optional)" value={reason} onChange={setReason} rows={2} />
          {issue && (
            <p className="field__error caption" role="alert">
              {issue}
            </p>
          )}
          {notice && (
            <p className="notice" role="status">
              {notice}
            </p>
          )}
          <div className="row">
            <Button variant="primary" type="submit" busy={busy}>
              {busy ? 'Granting access…' : 'Grant access'}
            </Button>
          </div>
        </form>
      ) : (
        <p className="caption muted">Your tenant admins grant and end Teslyn Support access.</p>
      )}

      <section className="panel stack" aria-label="Support record">
        <h2 className="heading">What Teslyn Support opened and changed</h2>
        {data.events.length === 0 ? (
          <p className="body muted">Nothing recorded.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="label">When</th>
                  <th className="label">What</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((ev) => (
                  <tr key={ev.id}>
                    <td className="caption">{when(ev.at)}</td>
                    <td className="body">{supportActionWords(ev.action, ev.target, ev.detail)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {past.length > 0 && (
          <p className="caption muted">
            Earlier grants: {past.length} ({past.filter((g) => g.first_used_at).length} opened).
          </p>
        )}
      </section>
    </div>
  );
}
